import { useEffect, useRef, useState } from "react";
import { stretchPassword } from "@timecapsule/crypto/password";
import { api, googleReauthUrl, type SignIn } from "./api";
import { isAborted, passkeysAvailable, signInAssertion } from "./passkey";
import { errorText, useT } from "./i18n";
import { navigate } from "./router";

/**
 * "Confirm it is you" (PLAN.md 4.16 K3): before a key is created or revoked the member signs in
 * again with one of their sign-in methods. Only this member's own sign-in counts (the server checks
 * it); success renews the session's sign-in time. `strong` (4.18 step 5): only the password or a
 * passkey; Google cannot re-confirm someone at an open computer. A member with neither is sent to
 * add a password (a mail link and the new password are a strong sign-in).
 */
export function ReauthDialog({ purpose, strong, onDone, onCancel }: { purpose: string; strong: boolean; onDone: (strong: boolean) => void; onCancel: () => void }) {
  const t = useT();
  const [methods, setMethods] = useState<SignIn[] | null>(null);
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const abort = useRef<AbortController | null>(null);
  useEffect(() => { api.signIns().then(setMethods, (err) => setError(errorText(err))); }, []);
  useEffect(() => {
    // The Google popup ends on /reauth-done, which reports back here.
    const onMessage = (event: MessageEvent) => {
      if (event.origin !== location.origin || event.data?.type !== "vrelis-reauth") return;
      if (event.data.ok) onDone(false);
      else { setError(t.reauth.mismatch); setBusy(false); }
    };
    addEventListener("message", onMessage);
    return () => removeEventListener("message", onMessage);
  }, [onDone, t]);

  const passwordLogin = methods?.find((m) => m.provider === "password");
  const hasPasskey = !!methods?.some((m) => m.provider === "passkey") && passkeysAvailable();
  const hasGoogle = !strong && !!methods?.some((m) => m.provider === "google");
  const noStrongMethod = strong && methods !== null && !passwordLogin?.label && !hasPasskey;

  async function run(fn: () => Promise<void>) {
    setBusy(true);
    setError(null);
    try {
      await fn();
      onDone(true);
    } catch (err) {
      if (!isAborted(err)) setError(errorText(err));
      setBusy(false);
    }
  }

  return (
    <div className="modal-backdrop">
      <div className="modal card" role="dialog" aria-modal="true" aria-labelledby="reauth-title">
        <h3 id="reauth-title">{t.reauth.title}</h3>
        <p>{t.reauth.text(purpose)}</p>
        {methods === null && !error && <p className="muted small">{t.common.loading}</p>}
        {noStrongMethod && (
          <>
            <p className="warn small">{t.reauth.needStrong}</p>
            <button type="button" className="primary" onClick={() => { onCancel(); navigate("/account"); }}>{t.reauth.openSignIns}</button>
          </>
        )}
        {passwordLogin?.label && (
          <form onSubmit={(e) => { e.preventDefault(); void run(async () => api.reauthPassword(passwordLogin.label!, await stretchPassword(password, passwordLogin.label!))); }}>
            <input type="password" autoComplete="current-password" aria-label={t.reauth.password} placeholder={t.reauth.password} value={password} disabled={busy} onChange={(e) => setPassword(e.target.value)} autoFocus />
            <button className="primary" type="submit" disabled={busy || !password}>{t.reauth.withPassword}</button>
          </form>
        )}
        <div className="modal-actions">
          {hasPasskey && (
            <button type="button" disabled={busy} onClick={() => void run(async () => {
              abort.current = new AbortController();
              const { challenge, token } = await api.passkeyOptions();
              await api.reauthPasskey(token, await signInAssertion(challenge, abort.current.signal));
            })}>{t.reauth.withPasskey}</button>
          )}
          {hasGoogle && (
            <button type="button" disabled={busy} onClick={() => {
              setError(null);
              setBusy(true);
              const popup = window.open(googleReauthUrl(), "vrelis-reauth", "width=520,height=680");
              if (!popup) { setError(t.reauth.popupBlocked); setBusy(false); }
            }}>{t.reauth.withGoogle}</button>
          )}
          <button type="button" onClick={() => { abort.current?.abort(); onCancel(); }}>{t.common.cancel}</button>
        </div>
        {error && <p className="error small">{error}</p>}
      </div>
    </div>
  );
}

/** End of the Google reauth popup: tells the page that opened it, then closes. */
export function ReauthDone() {
  const t = useT();
  useEffect(() => {
    const failed = new URLSearchParams(location.search).get("error");
    window.opener?.postMessage({ type: "vrelis-reauth", ok: !failed }, location.origin);
    if (window.opener) window.close();
  }, []);
  return <p className="narrow">{t.reauth.closeWindow}</p>;
}
